import React from "react";
import { PageHeader, SectionTitle, Pill } from "@/lib/editorial";
import { CHROME_IMAGES, STATUSES } from "@/data/mockData";
import { Bell, Server, Database, Terminal } from "lucide-react";

const PIPELINE_STEPS = [
  { n: 1, label: "Discover", text: "Research LinkedIn directly — search per offer/campaign ICP, LinkedIn's own \"People you may know\" suggestions, or a manually pasted profile URL. Insert as NEW. No Google Sheets involved anywhere in this pipeline." },
  { n: 2, label: "Classify", text: "An LLM judges sales-offer contacts as RESEARCHED or DISQUALIFIED. Campaign contacts (e.g. coffee-date) skip this — the discovery search itself was the filter, and \"would they buy our service\" is the wrong question for someone you want to meet for coffee." },
  { n: 3, label: "Invite", text: "Every RESEARCHED contact becomes invite-eligible immediately — no multi-day warmup, no scheduled calendar. invite_tick runs continuously and sends as guardrail budget (daily cap, spacing, working hours) allows. Moves to INVITED." },
  { n: 4, label: "Wait for acceptance", text: "relationship_check_tick re-checks INVITED contacts periodically (daily for the first two weeks, weekly after). Not accepting yet is never held against a contact — no auto-disqualify, no expiry. Once accepted: CONNECTED." },
  { n: 5, label: "Cold DM", text: "dm_tick queues the first message exactly 2 days after acceptance — not 2 days after the invite was sent. The message is connection-first by hard rule: no pitch, no naming what Unidex does, no call-ask. Its only job is to be a genuine first message. Moves to SENT once delivered." },
  { n: 6, label: "Follow-up", text: "If there's no reply after a short wait, dm_tick queues one follow-up — the first point where a low-pressure ask becomes appropriate. Moves to FOLLOWED_UP once delivered." },
  { n: 7, label: "Reply", text: "Any inbound reply surfaces in the Inbox. A human reviews and sends — there's no auto-send. CLOSED_LOST is the only status a human sets manually, and the only way a contact permanently exits rotation." },
];

const STATUS_DESCRIPTIONS = {
  NEW: "Just discovered or added, not yet judged.",
  DISQUALIFIED: "Failed the sales-fit check (offer contacts only — campaign contacts never land here).",
  RESEARCHED: "Qualified, no invitation sent yet. Eligible for invite_tick right now.",
  INVITED: "Invitation sent, not yet accepted. Stays here indefinitely — checked periodically, never auto-expired.",
  CONNECTED: "Invitation accepted. The 2-day cold-DM clock starts here.",
  SENT: "Cold DM delivered. Awaiting reply.",
  FOLLOWED_UP: "Follow-up DM delivered after no reply.",
  REPLIED: "Contact responded. Surfaces prominently in Inbox.",
  CLOSED_LOST: "Manually marked not interested/unfit — the only way a contact permanently exits rotation.",
};

const TIMER_SCHEDULE = [
  { name: "classify_tick", freq: "Every 30 min", desc: "Classifies NEW contacts, enriches missing profile data" },
  { name: "invite_tick", freq: "Every 15 min", desc: "Sends invitations to RESEARCHED contacts as budget allows (weighted across offers/campaigns)" },
  { name: "relationship_check_tick", freq: "Hourly sweep, tapered per-contact", desc: "Checks INVITED contacts for acceptance — daily for 14 days, weekly after" },
  { name: "dm_tick", freq: "Every 15 min", desc: "Queues cold DM 2 days post-acceptance; queues follow-up after a short no-reply wait" },
  { name: "send_tick", freq: "Every 10 min", desc: "Delivers whatever's queued, respecting guardrails" },
  { name: "discover_tick", freq: "Daily at 08:00 and 20:00 UTC", desc: "Researches new prospects — LinkedIn search, People You May Know, or manual add" },
  { name: "digest_tick", freq: "Daily at 20:00 UTC", desc: "Telegram summary: sent, queued, pipeline backlog by stage, session health" },
];

export default function HowItWorks() {
  return (
    <div>
      {/* Hero band */}
      <div className="bg-[var(--ink)] text-white relative overflow-hidden">
        <img
          src={CHROME_IMAGES.headerDecor}
          alt=""
          className="chrome-img absolute inset-0 w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--ink)] via-[var(--ink)]/70 to-transparent" />
        <div className="relative px-8 lg:px-14 py-20">
          <PageHeader
            eyebrow="UNIDEX manual"
            tape="How"
            rest="it works"
            sub="An editorial walk-through of the lead lifecycle, the timer schedule, the underlying infrastructure, and the alerts that keep the engine honest."
            dark
            testId="info-title"
          />
        </div>
      </div>

      <div className="px-8 lg:px-14 py-14 max-w-[1280px]">
        {/* Pipeline */}
        <SectionTitle eyebrow="01">Full pipeline</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-2">
          {PIPELINE_STEPS.map((s) => (
            <div key={s.n} className="border border-[var(--rule-light)] p-6 bg-white">
              <div className="flex items-baseline gap-4">
                <div className="font-display italic text-[60px] leading-none text-[var(--blue)]">{s.n}</div>
                <div>
                  <div className="font-display uppercase text-[20px] tracking-tight">{s.label}</div>
                  <p className="text-[13px] text-[var(--muted-light)] mt-2 leading-relaxed">{s.text}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Statuses */}
        <div className="mt-16">
          <SectionTitle eyebrow="02">Contact statuses</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            {STATUSES.map((s) => (
              <div key={s} className="flex items-start gap-4 border-b border-[var(--rule-light)] py-4">
                <Pill color="blue">{s}</Pill>
                <p className="text-[14px] text-[var(--muted-light)] leading-relaxed">
                  {STATUS_DESCRIPTIONS[s]}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Timer schedule */}
        <div className="mt-16">
          <SectionTitle eyebrow="03">Tick schedule</SectionTitle>
          <table className="w-full text-[13px] mt-2">
            <thead>
              <tr className="text-left border-b border-[var(--ink)]">
                <Th>Tick</Th>
                <Th>Frequency</Th>
                <Th>Description</Th>
              </tr>
            </thead>
            <tbody>
              {TIMER_SCHEDULE.map((t) => (
                <tr key={t.name} className="border-b border-[var(--rule-light)]">
                  <td className="py-3 pr-4 font-mono text-[12px]">{t.name}</td>
                  <td className="py-3 pr-4">{t.freq}</td>
                  <td className="py-3 pr-4 text-[var(--muted-light)]">{t.desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Infra */}
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <InfoCard icon={Server} title="Infrastructure" rows={[
            ["VPS", "76.13.156.191"],
            ["Domain", "coldoutreachforlinkin.xyz"],
            ["TLS", "Let's Encrypt, auto-renew"],
            ["DB", "/var/lib/outreach-v2/outreach.db"],
          ]} />
          <InfoCard icon={Database} title="LLM models" rows={[
            ["Generation", "gpt-4o-mini (OpenRouter key → kimi-k2)"],
            ["Classification", "gpt-4o-mini — never a reasoning model"],
            ["Why not reasoning for classification", "burns its token budget on chain-of-thought, returns truncated JSON"],
          ]} />
          <InfoCard icon={Bell} title="Telegram alerts" rows={[
            ["Session expired", "Sent immediately, agent auto-paused"],
            ["Circuit breaker tripped", "Sent immediately, run stopped"],
            ["Reply received", "Surfaces in Inbox"],
            ["Daily digest", "20:00 UTC"],
          ]} />
        </div>

        {/* SSH debug */}
        <div className="mt-16">
          <SectionTitle eyebrow="04">SSH debug commands</SectionTitle>
          <pre className="bg-[var(--ink)] text-white p-6 font-mono text-[12.5px] overflow-x-auto leading-relaxed">
{`# Tail service logs
journalctl -u outreach-v2 -f --since "30 min ago"

# Restart the engine (FastAPI + in-process scheduler)
sudo systemctl restart outreach-v2

# Inspect a contact in the DB
sqlite3 /var/lib/outreach-v2/outreach.db \\
  "SELECT id, full_name, status, offer_id, invitation_accepted_at FROM contacts WHERE id = 1;"

# Re-run a specific tick manually
cd /opt/outreach-v2/code && AGENT_ID=1 .venv/bin/python -m app.tick.invite_tick`}
          </pre>
        </div>
      </div>
    </div>
  );
}

function Th({ children }) {
  return <th className="text-left text-[10px] uppercase tracking-[0.22em] text-[var(--muted-light)] py-3 pr-4">{children}</th>;
}

function InfoCard({ icon: Icon, title, rows }) {
  return (
    <div className="border border-[var(--rule-light)] bg-white p-6">
      <div className="flex items-center gap-3 mb-4">
        <Icon className="w-4 h-4 text-[var(--blue)]" />
        <div className="font-display uppercase text-[18px] tracking-tight">{title}</div>
      </div>
      <dl className="space-y-2">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between border-b border-dashed border-[var(--rule-light)] py-1.5">
            <dt className="text-[12px] text-[var(--muted-light)]">{k}</dt>
            <dd className="text-[12px] font-mono">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
